import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, ChevronRight, RefreshCw, Clock, ChevronDown, ChevronUp } from "lucide-react";

// Feb 2026 (v3) — role-aware "waiting for you" banner.
//
//   • Queries GET /api/dashboard/pending on mount + every 30s.
//   • Auto-collapses when the pending queue has > COLLAPSE_THRESHOLD items so
//     the tab strip and project detail below never get pushed off-screen.
//   • Listens for a global `refresh-pending` custom event so any page mutation
//     (e.g. IMOS Manager self-assigns a reviewer) can force an instant reload
//     WITHOUT waiting for the 30s poll — matching the "no ghost notification"
//     requirement.
//   • Renders quiet-mode when the queue is empty (no banner at all).

const COLLAPSE_THRESHOLD = 3;

export default function PendingQueueBanner({ testid = "pending-queue-banner", limit = 20 }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const { data } = await api.get("/dashboard/pending");
      setData(data);
    } catch { /* fail-silent, banner just stays hidden */ }
    finally { setRefreshing(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    // Global event bus for instant refresh after a user action.
    const handler = () => load();
    window.addEventListener("refresh-pending", handler);
    return () => { clearInterval(t); window.removeEventListener("refresh-pending", handler); };
  }, []);

  if (!data || (data.items || []).length === 0) return null;

  const count = data.count;
  const shouldCollapse = count > COLLAPSE_THRESHOLD && !expanded;
  const items = shouldCollapse ? [] : data.items.slice(0, limit);
  const overflow = shouldCollapse ? 0 : Math.max(0, data.items.length - items.length);

  return (
    <div className="px-10 pt-6" data-testid={testid}>
      <div className="border border-[#d4af37]/30 bg-[#0d0b06] rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-5 py-3 border-b border-[#d4af37]/20 bg-[#d4af37]/5 flex items-center justify-between hover:bg-[#d4af37]/10 transition-colors"
          data-testid="pending-queue-toggle"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[#d4af37]"/>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
              Pending Actions · {count}
            </p>
            {count > COLLAPSE_THRESHOLD && (
              <span className="text-[9px] font-mono text-gray-500 ml-1 normal-case tracking-normal">
                {shouldCollapse ? "· click to expand" : "· click to collapse"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              onClick={(e) => { e.stopPropagation(); load(); }}
              className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-[#d4af37] flex items-center gap-1 cursor-pointer"
              data-testid="pending-queue-refresh"
              role="button"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`}/>
              Refresh
            </span>
            {shouldCollapse
              ? <ChevronDown className="w-3.5 h-3.5 text-[#d4af37]"/>
              : <ChevronUp className="w-3.5 h-3.5 text-[#d4af37]"/>}
          </div>
        </button>
        {!shouldCollapse && (
          <ul className="divide-y divide-[#1a1a1a]" data-testid="pending-queue-list">
            {items.map((it, idx) => (
              <li key={`${it.kind}-${it.id}-${idx}`} data-testid={`pending-item-${idx}`}>
                <Link to={it.route || "#"}
                       className="flex items-center justify-between px-5 py-3 hover:bg-[#111] transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{it.title}</p>
                    <p className="text-[10px] font-mono text-gray-500 mt-0.5 flex items-center gap-2">
                      {it.client && <span>{it.client}</span>}
                      {it.status && <span className="text-[#d4af37]">· {it.status}</span>}
                      {it.at && (
                        <span className="text-gray-600 flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5"/> {new Date(it.at).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0"/>
                </Link>
              </li>
            ))}
            {overflow > 0 && (
              <li className="px-5 py-2 text-[10px] font-mono text-gray-500 text-center">
                +{overflow} more…
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// Named helper so any mutation handler can just call refreshPending() after
// a successful POST/PATCH to erase ghost notifications immediately.
export function refreshPending() {
  window.dispatchEvent(new CustomEvent("refresh-pending"));
}
